import type { DataPointChoiceSource } from './dataPointChoices';
import type { ProgrammeDataRequirement } from './programmeDataRequirements';

export interface DataPoint {
	group: string;
	label: string;
	path: string;
	/**
	 * Where this point's allowed values come from, for points that hold one of a known set.
	 *
	 * Absent means free text, and a condition may then only ask whether it is filled in — an
	 * equality test typed by hand is the classic lookup-table failure, where the author writes
	 * "Scotland" against a column holding "GB-SCT" and the clause silently never appears.
	 */
	choices?: DataPointChoiceSource;
	// Derived from a per-investment list (holdings/loans): resolves to a value
	// only for investors who actually hold that investment, and to empty (or 0
	// for a sum) for those who don't. Surfaced in the picker so an admin knows
	// the blank may be empty for some investors.
	optional?: boolean;
	/** Programme switches that must all be on for investors to be asked for this data. */
	requires?: readonly ProgrammeDataRequirement[];
}

/**
 * A data point whose path is RELATIVE to one holding or one allocation, bound
 * by pinning a security rather than by position. The consumer composes
 * `data.<source> | where: "security.id", "<id>" | map: "<path>" | first <format>`.
 *
 * `source: 'either'` resolves against whichever list the transaction radio
 * selects, because both lists carry the same `security.*` projection.
 *
 * `format` is the COMPLETE trailing filter chain including its leading pipe.
 * It must sit after `| first`, never inside `map:` — `map:` takes a property
 * path only, so `map: "date | date: ..."` silently resolves to nothing.
 */
/**
 * The two lists a security-scoped expression can read: an investor's completed holdings, or
 * their in-flight allocations. Exported as a value so nothing has to restate the pair.
 */
export const SCOPED_SOURCES = ['holdings', 'allocations'] as const;

export type ScopedSource = (typeof SCOPED_SOURCES)[number];

/**
 * Sample values for the scoped data points: securityId -> source -> relative path -> value.
 *
 * The SOURCE is part of the key because a `source: 'either'` point is offered in both radio
 * positions and resolves against a different list in each — so one value per path could only
 * ever be right for one of them. A missing entry means "renders empty".
 */
export type ScopedSamples = Record<string, Partial<Record<ScopedSource, Record<string, string>>>>;

export interface ScopedDataPoint {
	group: string;
	label: string;
	path: string;
	source: ScopedSource | 'either';
	format?: string;
}

/** One programme security, as the field-binding editor's picker needs it. */
export interface ProgrammeSecurity {
	id: string;
	name: string;
	type: string;
	currency: string;
	/** The programme entity issuing it. A multi-entity programme offers every entity's securities. */
	issuerId: string;
	issuerName: string;
}

export const SECURITY_SCOPED_DATA_POINTS: readonly ScopedDataPoint[] = [
	// --- Security: the pinned security itself, in both radio modes ---------
	{ group: 'Security', label: 'Security name', path: 'security.name', source: 'either' },
	{ group: 'Security', label: 'Security type', path: 'security.type', source: 'either' },
	{ group: 'Security', label: 'Currency', path: 'security.currency', source: 'either' },
	{
		group: 'Security',
		label: 'Nominal value',
		path: 'security.nominalValue',
		source: 'either',
	},
	{
		group: 'Security',
		label: 'Votes per unit',
		path: 'security.votesPerUnit',
		source: 'either',
	},
	{ group: 'Security', label: 'Issuer name', path: 'security.issuer.name', source: 'either' },
	{
		group: 'Security',
		label: 'Issuer registered address',
		path: 'security.issuer.address.singleAddressString',
		source: 'either',
	},
	// --- Holding: a held position. `holdings`-only, because an unexecuted
	//     allocation has no balance, so these could only render blank there.
	{ group: 'Holding', label: 'Units held', path: 'unitsHeld', source: 'holdings' },
	{ group: 'Holding', label: 'Market value', path: 'fairMarketValue', source: 'holdings' },
	{
		group: 'Holding',
		label: 'Cost of acquisition',
		path: 'costOfAcquisition',
		source: 'holdings',
	},
	{ group: 'Holding', label: 'Votes', path: 'votes', source: 'holdings' },
	// --- Completed transaction --------------------------------------------
	{
		group: 'Completed transaction',
		label: 'Date',
		path: 'lastTransaction.date',
		source: 'holdings',
		format: '| date: "%d %b %Y"',
	},
	{
		group: 'Completed transaction',
		label: 'Type',
		path: 'lastTransaction.type',
		source: 'holdings',
		format: '| transaction_type',
	},
	{
		group: 'Completed transaction',
		label: 'Title',
		path: 'lastTransaction.title',
		source: 'holdings',
		format: '| transaction_title',
	},
	{
		group: 'Completed transaction',
		label: 'Units',
		path: 'lastTransaction.units',
		source: 'holdings',
	},
	{
		group: 'Completed transaction',
		label: 'Cost',
		path: 'lastTransaction.cost',
		source: 'holdings',
	},
	{
		group: 'Completed transaction',
		label: 'Buyer or seller',
		path: 'lastTransaction.holderRole',
		source: 'holdings',
		format: '| holder_role',
	},
	// --- Transaction in progress ------------------------------------------
	{
		group: 'Transaction in progress',
		label: 'Units',
		path: 'units',
		source: 'allocations',
	},
	{ group: 'Transaction in progress', label: 'Cost', path: 'cost', source: 'allocations' },
	{
		group: 'Transaction in progress',
		label: 'Expected type',
		path: 'expectedType',
		source: 'allocations',
		// ExpectedTransactionType's four members are name-identical to the
		// TransactionType members, so one label map serves both.
		format: '| transaction_type',
	},
	{
		group: 'Transaction in progress',
		label: 'Target date',
		path: 'targetDate',
		source: 'allocations',
		format: '| date: "%d %b %Y"',
	},
	{
		group: 'Transaction in progress',
		label: 'Investment type',
		path: 'investmentType',
		source: 'allocations',
		format: '| investment_type',
	},
] as const;

export const FIELD_BINDING_CATALOGUE: readonly DataPoint[] = [
	// --- Investor ---------------------------------------------------------
	{ group: 'Investor profile', label: 'First name', path: 'data.investor.firstName' },
	{ group: 'Investor profile', label: 'Last name', path: 'data.investor.lastName' },
	{ group: 'Investor profile', label: 'Email address', path: 'data.investor.email' },
	{ group: 'Investor profile', label: 'Phone number', path: 'data.investor.phoneNumber' },
	{
		group: 'Investor profile',
		label: 'Date of birth',
		path: 'data.investor.birthDate | date: "%d %b %Y"',
	},
	{ group: 'Registered address', label: 'Address line 1', path: 'data.investor.address.street' },
	{ group: 'Registered address', label: 'City', path: 'data.investor.address.city' },
	{ group: 'Registered address', label: 'Postcode', path: 'data.investor.address.postCode' },
	{
		group: 'Registered address',
		label: 'Country',
		path: 'data.investor.address.country',
		choices: 'countryName',
	},
	{
		group: 'Registered address',
		label: 'Full registered address',
		path: 'data.investor.address.singleAddressString',
	},
	{
		group: 'Investor KYC',
		label: 'Tax residency',
		path: 'data.investor.additionalDetails.taxResidency',
		requires: ['isTaxResidenceRequired'],
	},
	{
		group: 'Investor KYC',
		label: 'Tax identification number',
		path: 'data.investor.additionalDetails.tin',
		requires: ['isTaxResidenceRequired', 'isTinRequired'],
	},
	{
		group: 'Investor KYC',
		label: 'Country of birth',
		path: 'data.investor.additionalDetails.countryOfBirth',
		choices: 'countryCode',
		requires: ['isCountryOfBirthRequired'],
	},
	{
		group: 'Investor KYC',
		label: 'Country of birth (full name)',
		path: 'data.investor.additionalDetails.countryOfBirthName',
		choices: 'countryName',
		requires: ['isCountryOfBirthRequired'],
	},
	{
		group: 'Investor KYC',
		label: 'Place of birth',
		path: 'data.investor.additionalDetails.placeOfBirth',
		requires: ['isPlaceOfBirthRequired'],
	},
	// --- Tax residences (zero-to-many) ------------------------------------
	{
		group: 'Tax residences',
		label: 'Tax residence country',
		path: 'data.investor.taxResidences[0].country',
		choices: 'countryName',
		optional: true,
		requires: ['isTaxResidenceRequired'],
	},
	{
		group: 'Tax residences',
		label: 'Tax residence country code',
		path: 'data.investor.taxResidences[0].countryCode',
		choices: 'countryCode',
		optional: true,
		requires: ['isTaxResidenceRequired'],
	},
	{
		group: 'Tax residences',
		label: 'Tax identification number (TIN)',
		path: 'data.investor.taxResidences[0].number',
		optional: true,
		requires: ['isTaxResidenceRequired', 'isTinRequired'],
	},
	{
		group: 'Tax residences',
		label: 'Tax authority',
		path: 'data.investor.taxResidences[0].authority',
		optional: true,
		requires: ['isTaxResidenceRequired'],
	},
	{
		group: 'Tax residences',
		label: 'All tax residence countries',
		path: 'data.investor.taxResidences | map: "country" | join: ", "',
		optional: true,
		requires: ['isTaxResidenceRequired'],
	},
	{
		group: 'Tax residences',
		label: 'All tax identification numbers',
		path: 'data.investor.taxResidences | map: "number" | join: ", "',
		optional: true,
		requires: ['isTaxResidenceRequired', 'isTinRequired'],
	},
	// --- Nationality / citizenship (zero-to-many) --------------------------
	{
		group: 'Nationality',
		label: 'Nationality',
		path: 'data.investor.nationalities[0].nationality',
		optional: true,
		requires: ['isCitizenshipRequired'],
	},
	{
		group: 'Nationality',
		label: 'Country of citizenship',
		path: 'data.investor.nationalities[0].country',
		choices: 'countryName',
		optional: true,
		requires: ['isCitizenshipRequired'],
	},
	{
		group: 'Nationality',
		label: 'All nationalities',
		path: 'data.investor.nationalities | map: "nationality" | join: ", "',
		optional: true,
		requires: ['isCitizenshipRequired'],
	},
	{
		group: 'Nationality',
		label: 'All countries of citizenship',
		path: 'data.investor.nationalities | map: "country" | join: ", "',
		optional: true,
		requires: ['isCitizenshipRequired'],
	},
	// --- Employment (zero-to-many, primary record first) -------------------
	{
		group: 'Employment',
		label: 'Employer name',
		path: 'data.investor.employment[0].employerName',
		optional: true,
	},
	{
		group: 'Employment',
		label: 'Employer company number',
		path: 'data.investor.employment[0].employerNumber',
		optional: true,
	},
	{
		group: 'Employment',
		label: 'Employer address',
		path: 'data.investor.employment[0].employerAddress.singleAddressString',
		optional: true,
	},
	{
		group: 'Employment',
		label: 'Job title',
		path: 'data.investor.employment[0].jobTitle',
		optional: true,
	},
	{
		group: 'Employment',
		label: 'Employment start date',
		path: 'data.investor.employment[0].startDate | date: "%d %b %Y"',
		optional: true,
	},
	{
		group: 'Employment',
		label: 'Employment end date',
		path: 'data.investor.employment[0].endDate | date: "%d %b %Y"',
		optional: true,
	},
	{
		group: 'Employment',
		label: 'Employee reference',
		path: 'data.investor.employment[0].employeeReference',
		optional: true,
		requires: ['isEmployeeIdRequired'],
	},
	{
		group: 'Employment',
		label: 'All employers',
		path: 'data.investor.employment | map: "employerName" | join: ", "',
		optional: true,
	},
	{
		group: 'Investor bank account',
		label: 'Bank name',
		path: 'data.investor.bankAccount.bankName',
		requires: ['isInvestorBankAccountRequired'],
	},
	{
		group: 'Investor bank account',
		label: 'IBAN',
		path: 'data.investor.bankAccount.iban',
		requires: ['isInvestorBankAccountRequired'],
	},
	{
		group: 'Investor bank account',
		label: 'Account number',
		path: 'data.investor.bankAccount.accountNumber',
		requires: ['isInvestorBankAccountRequired'],
	},
	{
		group: 'Investor bank account',
		label: 'SWIFT / BIC',
		path: 'data.investor.bankAccount.swiftCode',
		requires: ['isInvestorBankAccountRequired'],
	},
	{
		group: 'Investor bank account',
		label: 'Account holder',
		path: 'data.investor.bankAccount.accountHolder',
		requires: ['isInvestorBankAccountRequired'],
	},
	// --- Representative ---------------------------------------------------
	{ group: 'Representative', label: 'First name', path: 'data.representative.firstName' },
	{ group: 'Representative', label: 'Last name', path: 'data.representative.lastName' },
	{ group: 'Representative', label: 'Email address', path: 'data.representative.email' },
	{
		group: 'Representative',
		label: 'Date of birth',
		path: 'data.representative.birthDate | date: "%d %b %Y"',
	},
	{
		group: 'Representative',
		label: 'Full address',
		path: 'data.representative.address.singleAddressString',
	},
	{
		group: 'Representative',
		label: 'Position in company',
		path: 'data.deal.projectRepresentativePerson.positionInCompany',
	},
	// --- Programme / MIP --------------------------------------------------
	{ group: 'Programme', label: 'Programme name', path: 'data.mip.name' },
	{ group: 'Programme', label: 'Currency', path: 'data.mip.currency' },
	{ group: 'Company', label: 'Company name', path: 'data.mip.entity.name' },
	{ group: 'Company', label: 'Company number', path: 'data.mip.entity.number' },
	{
		group: 'Company',
		label: 'Registered office address',
		path: 'data.mip.entity.address.singleAddressString',
	},
	{ group: 'Programme bank account', label: 'Bank name', path: 'data.mip.bankAccount.bankName' },
	{ group: 'Programme bank account', label: 'IBAN', path: 'data.mip.bankAccount.iban' },
	{
		group: 'Programme bank account',
		label: 'Account number',
		path: 'data.mip.bankAccount.accountNumber',
	},
	{
		group: 'Programme bank account',
		label: 'SWIFT / BIC',
		path: 'data.mip.bankAccount.swiftCode',
	},
	{
		group: 'Programme bank account',
		label: 'Account holder',
		path: 'data.mip.bankAccount.accountHolder',
	},
	// --- Investment vehicle (PIV) -----------------------------------------
	{ group: 'Investment vehicle', label: 'Vehicle name', path: 'data.vehicleData.pivName' },
	{
		group: 'Investment vehicle',
		label: 'Corporate number',
		path: 'data.vehicleData.corporateNumber',
	},
	{
		group: 'Investment vehicle',
		label: 'Vehicle address',
		path: 'data.vehicleData.address.singleAddressString',
	},
	// --- Holdings & securities (per-investment) ---------------------------
	{
		group: 'Holdings & securities',
		label: 'Security name (first holding — order not guaranteed)',
		path: 'data.holdings[0].security.name',
		optional: true,
	},
	{
		group: 'Holdings & securities',
		label: 'Security type (first holding — order not guaranteed)',
		path: 'data.holdings[0].security.type',
		optional: true,
	},
	{
		group: 'Holdings & securities',
		label: 'Units held (first holding — order not guaranteed)',
		path: 'data.holdings[0].unitsHeld',
		optional: true,
	},
	{
		group: 'Holdings & securities',
		label: 'Market value (first holding — order not guaranteed)',
		path: 'data.holdings[0].fairMarketValue',
		optional: true,
	},
	{
		group: 'Holdings & securities',
		label: 'Cost of acquisition (first holding — order not guaranteed)',
		path: 'data.holdings[0].costOfAcquisition',
		optional: true,
	},
	{
		group: 'Holdings & securities',
		label: 'Votes (first holding — order not guaranteed)',
		path: 'data.holdings[0].votes',
		optional: true,
	},
	{
		group: 'Holdings & securities',
		label: 'Issuer name (first holding — order not guaranteed)',
		path: 'data.holdings[0].security.issuer.name',
		optional: true,
	},
	{
		group: 'Holdings & securities',
		label: 'Total units held (all holdings)',
		path: 'data.holdings | map: "unitsHeld" | sum',
		optional: true,
	},
	{
		group: 'Holdings & securities',
		label: 'Total market value (all holdings)',
		path: 'data.holdings | map: "fairMarketValue" | sum',
		optional: true,
	},
	// --- Loans (per-investment) -------------------------------------------
	{
		group: 'Loans',
		label: 'Loan value (first loan — order not guaranteed)',
		path: 'data.loans[0].totalValue',
		optional: true,
	},
	{
		group: 'Loans',
		label: 'Funder name (first loan — order not guaranteed)',
		path: 'data.loans[0].funder.name',
		optional: true,
	},
	{
		group: 'Loans',
		label: 'Total loan value (all loans)',
		path: 'data.loans | map: "totalValue" | sum',
		optional: true,
	},
	// --- Document ---------------------------------------------------------
	{ group: 'Document', label: "Today's date", path: 'data.currentDate | date: "%d %b %Y"' },
	// --- Investor declarations --------------------------------------------
	{
		group: 'Investor declarations',
		label: 'Is politically exposed person',
		path: 'data.declarations.isPoliticallyExposed',
		requires: ['isKycRequired', 'isPepRequired'],
	},
	{
		group: 'Investor declarations',
		label: 'Is beneficial owner',
		path: 'data.declarations.isBeneficialOwner',
		requires: ['isKycRequired', 'isBeneficialOwnershipRequired'],
	},
	{
		group: 'Investor declarations',
		label: 'Source of funds: salary',
		path: 'data.declarations.fundsSource.salary',
		requires: ['isKycRequired', 'isSourceOfFundsRequired'],
	},
	{
		group: 'Investor declarations',
		label: 'Source of funds: personal savings',
		path: 'data.declarations.fundsSource.personalSavings',
		requires: ['isKycRequired', 'isSourceOfFundsRequired'],
	},
	{
		group: 'Investor declarations',
		label: 'Source of funds: investment income',
		path: 'data.declarations.fundsSource.investmentIncome',
		requires: ['isKycRequired', 'isSourceOfFundsRequired'],
	},
	{
		group: 'Investor declarations',
		label: 'Source of funds: inheritance',
		path: 'data.declarations.fundsSource.inheritance',
		requires: ['isKycRequired', 'isSourceOfFundsRequired'],
	},
	{
		group: 'Investor declarations',
		label: 'Source of funds: other',
		path: 'data.declarations.fundsSource.other',
		requires: ['isKycRequired', 'isSourceOfFundsRequired'],
	},
	// --- Cohorts (zero-to-many) -------------------------------------------
	{
		group: 'Cohorts',
		label: 'All cohort names',
		path: 'data.investor.cohorts | map: "name" | join: ", "',
		optional: true,
	},
	// No cohort-tags scalar at all. "All tags across all cohorts" cannot be one — `cohorts |
	// map: "tags"` yields an array of arrays, which `join` stringifies as "[object Object]" —
	// and a `cohorts[0]` one would be positional, which is the class of binding this catalogue
	// is moving AWAY from: an investor's cohort order is not meaningful, so index 0 denotes a
	// different cohort for every recipient. Per-cohort name-and-tags is the `cohorts` collection
	// below; one specific cohort's tags is the `Tags in <scheme>` scheme data point.
] as const;

/**
 * Whether a point reads one holding by its place in the investor's list. That place is a
 * different security for each investor, so a programme that offers the security picker does
 * not offer these: pinning the security is how an author says which holding they mean.
 */
export const isFirstHoldingPoint = (path: string): boolean => path.startsWith('data.holdings[0]');

export function buildLiquidExpression(paths: string[]): string {
	return paths.map((p) => `{{ ${p} }}`).join(' ');
}

// --- Repeating-list collections ------------------------------------------
// A "collection" is a repeating list (holdings, loans, …) an admin can bind a
// blank to so it renders one segment per item via a LiquidJS `{% for %}` loop.
// Distinct from the scalar FIELD_BINDING_CATALOGUE above (which binds a blank
// to a single data point). `itemFields[].path` is RELATIVE to the loop item
// (the loop variable is always `item`), e.g. "unitsHeld" → `{{ item.unitsHeld }}`.
export interface CollectionItemField {
	label: string;
	path: string; // path RELATIVE to the loop item, e.g. "unitsHeld", "security.name"
	/** Switches this field needs beyond its collection's. */
	requires?: readonly ProgrammeDataRequirement[];
}

export interface FieldBindingCollection {
	key: string; // e.g. "holdings"
	label: string; // e.g. "Holding"
	source: string; // Liquid list path, e.g. "data.holdings"
	itemFields: readonly CollectionItemField[];
	/** Programme switches that must all be on for investors to be asked for this list. */
	requires?: readonly ProgrammeDataRequirement[];
}

export const FIELD_BINDING_COLLECTIONS: readonly FieldBindingCollection[] = [
	{
		key: 'holdings',
		label: 'Holding',
		source: 'data.holdings',
		itemFields: [
			{ label: 'Units held', path: 'unitsHeld' },
			{ label: 'Security name', path: 'security.name' },
			{ label: 'Security type', path: 'security.type' },
			{ label: 'Market value', path: 'fairMarketValue' },
			{ label: 'Cost of acquisition', path: 'costOfAcquisition' },
			{ label: 'Votes', path: 'votes' },
			{ label: 'Issuer name', path: 'security.issuer.name' },
			// The date filter belongs IN the relative path: these are real Date
			// objects at generation time, and Liquid stringifies an unfiltered
			// Date as "Mon Nov 03 2025 00:00:00 GMT+0000 (…)".
			{ label: 'Last transaction date', path: 'lastTransaction.date | date: "%d %b %Y"' },
			{ label: 'Last transaction units', path: 'lastTransaction.units' },
		],
	},
	{
		key: 'loans',
		label: 'Loan',
		source: 'data.loans',
		itemFields: [
			{ label: 'Loan value', path: 'totalValue' },
			{ label: 'Funder name', path: 'funder.name' },
		],
	},
	{
		key: 'taxResidences',
		label: 'Tax residence',
		source: 'data.investor.taxResidences',
		requires: ['isTaxResidenceRequired'],
		itemFields: [
			{ label: 'Country', path: 'country' },
			{ label: 'Country code', path: 'countryCode' },
			{ label: 'Tax identification number', path: 'number', requires: ['isTinRequired'] },
			{ label: 'Tax authority', path: 'authority' },
		],
	},
	{
		key: 'nationalities',
		label: 'Nationality',
		source: 'data.investor.nationalities',
		requires: ['isCitizenshipRequired'],
		itemFields: [
			{ label: 'Nationality', path: 'nationality' },
			{ label: 'Country', path: 'country' },
			{ label: 'Country code', path: 'countryCode' },
		],
	},
	{
		key: 'employment',
		label: 'Employment record',
		source: 'data.investor.employment',
		itemFields: [
			{ label: 'Employer name', path: 'employerName' },
			{ label: 'Employer company number', path: 'employerNumber' },
			{ label: 'Employer address', path: 'employerAddress.singleAddressString' },
			{ label: 'Job title', path: 'jobTitle' },
			{ label: 'Start date', path: 'startDate' },
			{ label: 'End date', path: 'endDate' },
			{
				label: 'Employee reference',
				path: 'employeeReference',
				requires: ['isEmployeeIdRequired'],
			},
		],
	},
	{
		key: 'cohorts',
		label: 'Cohort',
		source: 'data.investor.cohorts',
		itemFields: [
			{ label: 'Cohort name', path: 'name' },
			// The relative path carries its own filters. ITEM_FIELD_RE tolerates
			// this (it excludes only `}`), so the loop still round-trips.
			{ label: 'Tags', path: 'tags | map: "name" | join: ", "' },
		],
	},
	{
		key: 'allocations',
		label: 'Allocation in progress',
		source: 'data.allocations',
		itemFields: [
			{ label: 'Security name', path: 'security.name' },
			{ label: 'Security type', path: 'security.type' },
			{ label: 'Units', path: 'units' },
			{ label: 'Cost', path: 'cost' },
			{ label: 'Expected type', path: 'expectedType | transaction_type' },
			{ label: 'Target date', path: 'targetDate | date: "%d %b %Y"' },
		],
	},
] as const;

// Sentinel investor id for the built-in "sample investor". Selected by default
// in the field-binding editor so sample values and the live preview work
// immediately without picking a real investor. It is intentionally NOT a UUID
// so it can never collide with a real investor id.
export const SAMPLE_INVESTOR_ID = '__sample_investor__';

// Obviously-fake data for the sample investor. Shaped to match the `data` root
// that binding templates render against (mirrors the resolvable subset of
// SmartFieldsAvailableSources), so the same LiquidJS path resolves sample
// values in the catalogue and the merged preview. Values are deliberately
// self-describing so it's clear this is a demo. It intentionally includes two
// holdings and two loans so the (per-investment) list data points and the
// repeating-list ("for each") binding mode both preview with real repetition.
export const SAMPLE_INVESTOR_DATA = {
	investor: {
		firstName: 'Sample',
		lastName: 'Investor',
		email: 'sample.investor@example.com',
		phoneNumber: '+44 20 7946 0000',
		birthDate: '1985-03-14',
		address: {
			street: '123 Example Street',
			city: 'Sampletown',
			postCode: 'SAMPLE1',
			country: 'Demoland',
			singleAddressString: '123 Example Street, Sampletown, SAMPLE1, Demoland',
		},
		additionalDetails: {
			taxResidency: 'Demoland',
			tin: 'TIN-0000-DEMO',
			countryOfBirth: 'DM',
			countryOfBirthName: 'Demoland',
			placeOfBirth: 'Sampletown',
		},
		taxResidences: [
			{
				countryCode: 'DM',
				country: 'Demoland',
				authority: 'Demoland Revenue Office (DEMO)',
				number: 'TIN-0000-DEMO',
			},
			{
				countryCode: 'SM',
				country: 'Sampleland',
				authority: 'Sampleland Tax Authority (DEMO)',
				number: 'TIN-1111-DEMO',
			},
		],
		nationalities: [
			{ countryCode: 'DM', country: 'Demoland', nationality: 'Demolandish (DEMO)' },
			{ countryCode: 'SM', country: 'Sampleland', nationality: 'Samplelandic (DEMO)' },
		],
		cohorts: [
			{
				name: 'Sample Cohort (DEMO)',
				tags: [
					{ name: 'Tranche A (DEMO)' },
					{ name: 'Vested (DEMO)' },
					{ name: 'Board (DEMO)' },
				],
			},
			{ name: 'Second Cohort (DEMO)', tags: [{ name: 'Tranche B (DEMO)' }] },
		],
		schemes: [
			{ id: 'demo-scheme-tier', name: 'Tier (DEMO)', cohorts: [{ name: 'Tier 3' }] },
			{
				id: 'demo-scheme-region',
				name: 'Region (DEMO)',
				cohorts: [{ name: 'EMEA' }, { name: 'UK' }],
			},
		],
		employment: [
			{
				employerName: 'Sample Employer Ltd (DEMO)',
				employerNumber: 'EMPLOYER-0000',
				employerAddress: {
					singleAddressString: '4 Employer Way, Sampletown, SAMPLE4, Demoland',
				},
				jobTitle: 'Head of Sample Operations',
				startDate: '2019-04-01',
				endDate: '2024-12-31',
				employeeReference: 'EMP-0000-DEMO',
				isPrimary: true,
			},
			{
				employerName: 'Second Employer LLP (DEMO)',
				employerNumber: 'EMPLOYER-1111',
				employerAddress: {
					singleAddressString: '5 Former Street, Sampletown, SAMPLE5, Demoland',
				},
				jobTitle: 'Sample Analyst',
				startDate: '2014-09-15',
				endDate: '2019-03-31',
				employeeReference: 'EMP-1111-DEMO',
				isPrimary: false,
			},
		],
		bankAccount: {
			bankName: 'Sample Bank (DEMO)',
			iban: 'GB00SAMPLE00000000000000',
			accountNumber: '00000000',
			swiftCode: 'SAMPGB2LXXX',
			accountHolder: 'Sample Investor',
		},
	},
	representative: {
		firstName: 'Sample',
		lastName: 'Representative',
		email: 'sample.rep@example.com',
		birthDate: '1980-06-15',
		address: {
			singleAddressString: '1 Rep Road, Sampletown, SAMPLE2, Demoland',
		},
	},
	currentDate: '2026-01-15',
	deal: {
		projectRepresentativePerson: {
			positionInCompany: 'Director (DEMO)',
		},
	},
	declarations: {
		isPoliticallyExposed: false,
		isBeneficialOwner: true,
		fundsSource: {
			salary: true,
			personalSavings: true,
			investmentIncome: false,
			inheritance: false,
			other: false,
		},
	},
	mip: {
		name: 'Sample Programme (DEMO)',
		currency: 'GBP',
		entity: {
			name: 'Sample Company Ltd (DEMO)',
			number: 'SAMPLE-0000',
			address: {
				singleAddressString: '123 Example Street, Sampletown, SAMPLE1, Demoland',
			},
		},
		bankAccount: {
			bankName: 'Sample Bank (DEMO)',
			iban: 'GB00SAMPLE11111111111111',
			accountNumber: '11111111',
			swiftCode: 'SAMPGB2LXXX',
			accountHolder: 'Sample Company Ltd',
		},
	},
	vehicleData: {
		pivName: 'Sample Vehicle SPV (DEMO)',
		corporateNumber: 'SPV-0000',
		address: {
			singleAddressString: '2 Vehicle View, Sampletown, SAMPLE3, Demoland',
		},
	},
	holdings: [
		{
			unitsHeld: 1000,
			fairMarketValue: 25000,
			costOfAcquisition: 20000,
			votes: 1000,
			security: {
				id: 'demo-security-ordinary',
				name: 'Sample Ordinary Shares (DEMO)',
				type: 'Ordinary',
				currency: 'GBP',
				nominalValue: 1,
				votesPerUnit: 1,
				issuer: {
					name: 'Sample Company Ltd (DEMO)',
					number: 'SAMPLE-0000',
					address: {
						singleAddressString: '123 Example Street, Sampletown, SAMPLE1, Demoland',
					},
				},
			},
			lastTransaction: {
				id: 'demo-transaction-1',
				date: '2025-11-03',
				type: 'ISSUE',
				title: 'BENEFICIAL_INTEREST_AND_LEGAL_TITLE',
				units: 1000,
				cost: 20000,
				holderRole: 'buyer',
			},
			transactions: [{ date: '2025-11-03', units: 1000, cost: 20000, holderRole: 'buyer' }],
		},
		{
			unitsHeld: 500,
			fairMarketValue: 12000,
			costOfAcquisition: 10000,
			votes: 0,
			security: {
				id: 'demo-security-preference',
				name: 'Sample Preference Shares (DEMO)',
				type: 'Preference',
				currency: 'GBP',
				nominalValue: 1,
				votesPerUnit: 0,
				issuer: {
					name: 'Sample Company Ltd (DEMO)',
					number: 'SAMPLE-0000',
					address: {
						singleAddressString: '123 Example Street, Sampletown, SAMPLE1, Demoland',
					},
				},
			},
			lastTransaction: {
				id: 'demo-transaction-2',
				date: '2026-02-17',
				type: 'TRANSFER',
				title: 'BENEFICIAL_INTEREST_AND_LEGAL_TITLE',
				units: 500,
				cost: 10000,
				holderRole: 'buyer',
			},
			transactions: [{ date: '2026-02-17', units: 500, cost: 10000, holderRole: 'buyer' }],
		},
	],
	allocations: [
		{
			security: {
				id: 'demo-security-ordinary',
				name: 'Sample Ordinary Shares (DEMO)',
				type: 'Ordinary',
				currency: 'GBP',
				nominalValue: 1,
				votesPerUnit: 1,
				issuer: {
					name: 'Sample Company Ltd (DEMO)',
					number: 'SAMPLE-0000',
					address: {
						singleAddressString: '123 Example Street, Sampletown, SAMPLE1, Demoland',
					},
				},
			},
			units: 250,
			cost: 5000,
			expectedType: 'ISSUE',
			targetDate: '2026-06-01',
			investmentType: 'ALLOCATION',
		},
		{
			security: {
				id: 'demo-security-growth',
				name: 'Sample Growth Shares (DEMO)',
				type: 'Growth',
				currency: 'GBP',
				nominalValue: 1,
				votesPerUnit: 0,
				issuer: {
					name: 'Sample Company Ltd (DEMO)',
					number: 'SAMPLE-0000',
					address: {
						singleAddressString: '123 Example Street, Sampletown, SAMPLE1, Demoland',
					},
				},
			},
			units: 100,
			cost: 1000,
			expectedType: 'ISSUE',
			targetDate: '2026-07-01',
			investmentType: 'JOINER',
		},
	],
	loans: [
		{ totalValue: 50000, funder: { name: 'Sample Funder Ltd (DEMO)' } },
		{ totalValue: 15000, funder: { name: 'Second Funder LLP (DEMO)' } },
	],
} as const;
